<template>
    <div data-component="AmsPriceIndicatorModalComparisonCriteria">
        <div class="mb-16 text-h3 font-bold-headline">
            {{ comparisonCriteria.headline }}
        </div>

        <div
            v-for="(criterionGroup, i) in comparisonCriteria.criterionGroup"
            :key="criterionGroup.subheadline"
            class="flex flex-col gap-16 text-h4"
            :class="{ 'screen-m:mb-40 mb-32': i === 0 && hasRating }"
        >
            <div class="font-bold">{{ criterionGroup.subheadline }}</div>
            <div v-for="criteria in criterionGroup.criteria" :key="criteria.value" class="flex">
                <component
                    :is="criterionGroup.icon"
                    class="w-24 h-24 mr-8 min-w-24 min-h-24"
                    :class="{
                        'text-color-alert-positive-100': criterionGroup.color === IconColor.Green,
                        'text-color-alert-negative-100': criterionGroup.color === IconColor.Red,
                    }"
                />
                <div v-if="criteria.label" class="mr-4 font-bold">{{ criteria.label }}:</div>
                <div>{{ criteria.value }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'
import { ComparisonCriteria, CriterionGroup, IconColor } from './AmsPriceIndicatorModal.interface'
import { AmsItem } from '../../../services/amsSearch/amsSearch.interface'
import { MsError, MsCheckCircle } from '@sunrise/icons'
import { commonUtils } from '@sunrise/shared'

const props = defineProps({
    item: {
        type: Object as PropType<AmsItem>,
        required: true,
    },
    hasRating: Boolean,
})

const createComparisonCriteria = (item: AmsItem): ComparisonCriteria => {
    return props.hasRating
        ? { headline: 'Vergleichskriterien', criterionGroup: [comparedCriteria(item), nonassessableCriteria()] }
        : { criterionGroup: [noRatingCriteria()] }
}

const comparedCriteria = (item: AmsItem) => {
    return {
        subheadline: 'Welche Kriterien wir für dich verglichen haben:',
        icon: MsCheckCircle,
        color: IconColor.Green,
        criteria: [
            { label: 'Marke', value: item.make },
            { label: 'Modell', value: item.model },
            { label: 'Erstzulassung', value: item.initialRegistrationDate },
            { label: 'Kilometerstand', value: commonUtils.thousandSeparator(item.mileage) },
            { label: 'Fahrzeugzustand', value: item.usageState },
        ],
    }
}

const nonassessableCriteria = (): CriterionGroup => {
    return {
        subheadline: 'Welche Kriterien wir nicht beurteilen können:',
        icon: MsError,
        color: IconColor.Red,
        criteria: [
            { value: 'Wie das Auto aussieht' },
            { value: 'Der aktuelle Zustand des Autos' },
            { value: 'Wie das Auto für den Verkauf aufbereitet wurde' },
            { value: 'Zusätzliche Ausstattung' },
        ],
    }
}

const noRatingCriteria = (): CriterionGroup => {
    return {
        subheadline: 'Mögliche Gründe für fehlende Bewertung',
        icon: MsError,
        color: IconColor.Red,
        criteria: [
            { value: 'Spezielle Fahrzeuge (z. B. Oldtimer)' },
            { value: 'Keine vollständigen Angaben im Inserat' },
            { value: 'Nicht genügend Vergleichsmodelle' },
            { value: 'Inserate, die stark vom Marktpreis abweichen und erst geprüft werden müssen' },
        ],
    }
}

const comparisonCriteria = createComparisonCriteria(props.item)
</script>
